import * as React from "react";
import {FormControl, FormGroup, InputGroup} from "react-bootstrap";
import {Spinner} from "../../common/ui/spinner/Spinner";
import {LcdIcon} from "../../common/ui/icon/LcdIcon";
import AlertBox, {AlertType} from "../../common/ui/alert/AlertBox";
import {defineMessages, InjectedIntl} from "react-intl";
import {getErrorMessage} from "../../common/util/Util";

const TEXT_BOX_VALIDATION_MESSAGES = defineMessages({
  error: {
    id: "studio.UI.validated-text-box.submit-error",
    defaultMessage: "Operation failed due to an error: {errorMessage}"
  }
});

export type ValidationState = AlertType | null;

export type Validation = { state: ValidationState, message: string | null };

interface ValidatedTextBoxProps {
  value: string;
  fieldName: string;
  onChange?: (value: string) => void;
  onSubmit: (value: string) => Promise<Validation>;
  clientValidation?: (value: string, fieldName: string) => Validation; // Made optional
  placeholder?: string;
  className?: string;
  intl?: InjectedIntl; // Optional for internationalization support
}

interface ValidatedTextBoxState {
  editingValue: string;
  isSubmitting: boolean;
  validation: Validation;
  isMouseEnter: boolean;
  isEditing: boolean;
  showPopup: boolean;
}

class ValidatedTextBox extends React.Component<ValidatedTextBoxProps, ValidatedTextBoxState> {

  private static readonly DEFAULT_POPUP_TIMEOUT = 3000; // 3 seconds
  private popupTimer: NodeJS.Timeout | null = null;

  constructor(props: ValidatedTextBoxProps) {
    super(props);
    this.state = {
      editingValue: props.value,
      isSubmitting: false,
      validation: {state: null, message: null},
      isMouseEnter: false,
      isEditing: false,
      showPopup: false,
    };
  }

  componentDidUpdate(prevProps: ValidatedTextBoxProps, prevState: ValidatedTextBoxState) {
    if (this.state.validation.state && this.state.validation.state !== prevState.validation.state) {
      this.displayPopup(this.state.validation.state);
    }
  }

  private displayPopup(validationState: ValidationState) {
    const popupTimeout = validationState === "error" ? 4000 : ValidatedTextBox.DEFAULT_POPUP_TIMEOUT;
    this.setState({showPopup: true});
    this.clearPopupTimer();
    this.popupTimer = setTimeout(() => {
      this.hidePopup();
    }, popupTimeout);
  }

  componentWillUnmount() {
    this.clearPopupTimer();
  }

  clearPopupTimer = () => {
    if (this.popupTimer) {
      clearTimeout(this.popupTimer);
      this.popupTimer = null;
    }
  };

  hidePopup = () => {
    this.setState({showPopup: false});
  };

  handleChange = (e) => {
    const value = e.target.value;
    this.setState({isEditing: true, editingValue: value, validation: {state: null, message: null}});
    if (this.props.onChange) {
      this.props.onChange(value);
    }
  };

  handleBlur = async (intl?: InjectedIntl) => {
    await this.handleSubmit(intl);
  };

  handleKeyPress = async (event, intl?: InjectedIntl) => {
    const isEnterKey = event.key === "Enter"; // for macOS/Linux/Windows
    if (isEnterKey) {
      this.setState({isEditing: false, isMouseEnter: false});
      await this.handleSubmit(intl);
    }
  };

  handleSubmit = async (intl?: InjectedIntl) => {
    this.setState({isEditing: false, validation: {state: null, message: null}});
    if (this.state.editingValue !== this.props.value) {
      const {editingValue} = this.state;
      if (this.props.clientValidation) {
        const clientSideValidation = this.props.clientValidation(editingValue, this.props.fieldName);
        if (clientSideValidation.state === "error") {
          this.displayPopup("error");
          this.setState({validation: clientSideValidation});
          return;
        }
      }

      try {
        this.setState({isSubmitting: true, validation: {state: null, message: null}});
        this.setState({validation: await this.props.onSubmit(editingValue)})
      } catch (err) {
        const messageContent = "Operation failed";
        this.setState({
          validation: {
            state: "error",
            message: intl?.formatMessage(TEXT_BOX_VALIDATION_MESSAGES.error, {errorMessage: getErrorMessage(err)}) ??
                     messageContent
          }
        });
      } finally {
        this.setState({isSubmitting: false});
      }
    }
  }

  handleFocus = () => {
    this.setState({isEditing: true});
  };

  handleMouseEnter = () => {
    if (this.state.validation.state !== "error") {
      this.setState({isMouseEnter: true, validation: {state: null, message: null}});
    } else {
      this.setState({isMouseEnter: true});
    }
  };

  handleMouseLeave = () => {
    this.setState({isMouseEnter: false});
  };

  handleAddonMouseEnter = () => {
    this.setState({isMouseEnter: false});
  };

  render() {
    const {editingValue, isSubmitting, validation, isMouseEnter, isEditing, showPopup} = this.state;
    const {intl} = this.props;
    const validationState = validation.state;
    const showInput = isMouseEnter || isEditing || validationState === "error";

    return (
        <FormGroup validationState={validationState}>
          <div
              onMouseEnter={this.handleMouseEnter}
              onMouseLeave={this.handleMouseLeave}
          >
            <InputGroup className={this.props.className}>
              {
                showInput ? (
                    <>
                      <FormControl
                          type="text"
                          value={editingValue}
                          placeholder={this.props.placeholder}
                          onChange={this.handleChange}
                          onBlur={() => this.handleBlur(intl)}
                          onKeyPress={(evt) => this.handleKeyPress(evt, intl)}
                          onFocus={this.handleFocus}
                          disabled={isSubmitting}
                          autoFocus={isEditing}
                      />
                      <InputGroup.Addon
                          onMouseEnter={this.handleAddonMouseEnter}
                      >
                        {isSubmitting ? <Spinner/> : <LcdIcon icon="pencil"/>}
                      </InputGroup.Addon>
                    </>
                ) : (
                    <FormControl
                        type="text"
                        value={editingValue}
                        readOnly
                        style={{background: "transparent", border: "none", boxShadow: "none"}}
                        tabIndex={-1}
                    />
                )
              }
            </InputGroup>
          </div>
          {showPopup && validation.state && (
              <AlertBox type={validation.state} message={validation.message} onClose={() => {
                this.clearPopupTimer();
                this.hidePopup();
              }}/>
          )}
        </FormGroup>
    );
  }
}

export default ValidatedTextBox;